import { fn, get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { importSync } from '@embroider/macros';
import Component from '@glimmer/component';

import can from 'ember-can/helpers/can';
import cannot from 'ember-can/helpers/cannot';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';

import Button from 'umg-ember-backend/components/ui/button/index';
import Modal from 'umg-ember-backend/components/ui/modal/index';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import styles from './styles.module.css';

const bem = getBem(styles);

export default class UiFormFieldBelongsToComponent extends Component {
  // Services
  @service breadcrumb;
  @service recordRelationships;
  @service router;
  @service store;

  // Defaults
  @storeOnRoute('args.fieldId') isRecordListOpen = false;
  @storeOnRoute('args.fieldId') scrollPositionAssign;

  // Getter and setter
  get labelProperty() {
    return this.args.labelProperty ?? 'displayLabel';
  }

  get on() {
    return {
      openNewRecord: this.openNewRecord,
      openRecord: this.openRecord,
      openRecordList: this.openRecordList,
      assignRecords: this.assignRecords,
      removeRecord: this.removeRecord,
      ...this.args.on,
    };
  }

  get RecordExplorerForAssign() {
    const type = this.relationType ?? 'article';

    const module = importSync(`../../../../record-explorer/for/${type}`);

    return module.default;
  }

  get relationship() {
    const { property, model } = this.args;
    const recordType = getRecordType(model);

    // For some forms, we use fake models (embed-code, tool-itunes-import, ...)
    // whiche can't be found in the schema.
    if (!this.store.schema.hasResource({ type: recordType })) {
      return null;
    }

    const fields = this.store.schema.fields({
      type: recordType,
    });

    return fields.get(property);
  }

  get relationType() {
    return this.relationship?.type;
  }

  get relationKind() {
    return this.relationship?.kind;
  }

  // Functions
  assignRecords = (records) => {
    this.setRelation(records);
    this.closeRecordList();
  };

  closeRecordList = () => {
    this.isRecordListOpen = false;
    this.scrollPositionAssign = 0;
  };

  openNewRecord = async (
    model,
    relationKind,
    relationType,
    property,
    filters = {},
  ) => {
    let newRecord = await this.store.createRecord(relationType);
    this.setRelation([newRecord]);

    const modelFields = this.store.schema.fields({ type: relationType });
    for (let key of Object.keys(filters)) {
      if (!modelFields.has(key)) {
        continue;
      }

      const value = filters[key];
      newRecord[key] = Array.isArray(value) ? value[0] : value;
    }

    let options = {};
    options.beforeRollback = async () => {
      if (relationKind === 'belongsTo') {
        this.recordRelationships.watch(model, property, () => {
          model[property] = undefined;
        });
      } else {
        let existingRecords = await model[property];
        let indexOfRecord = existingRecords.indexOf(newRecord);
        existingRecords.splice(indexOfRecord, 1);
      }
    };

    let route = this.breadcrumb.registerDetailRoute(newRecord, options);
    this.router.transitionTo(...route.transitionToParams);
  };

  openRecord = async (record) => {
    let loadedRecord = await record;

    let route = this.breadcrumb.registerDetailRoute(loadedRecord);
    this.router.transitionTo(...route.transitionToParams);
  };

  openRecordList = () => {
    this.isRecordListOpen = true;
  };

  removeRecord = (record, model, relationKind, relationField) => {
    this.recordRelationships.watch(model, relationField, () => {
      model[relationField] = undefined;
    });
  };

  setRelation = (records) => {
    let { model, property } = this.args;

    this.recordRelationships.watch(model, property, () => {
      model[property] = records[0];
    });
  };

  // Template
  <template>
    <div
      class={{bem (hash is-disabled=@isDisabled)}}
      id={{@fieldId}}
      ...attributes
    >
      {{#if @value}}
        <div class={{bem 'value'}}>
          {{#if (has-block)}}
            {{yield @value this.labelProperty}}
          {{else}}
            {{get @value this.labelProperty}}
          {{/if}}
        </div>
        <div class={{bem 'actions'}}>
          {{#if this.on.openRecord}}
            <Button
              @disabled={{cannot 'read' @model parentModel=@model}}
              @icon='edit'
              @style='outline'
              {{on 'click' (fn this.on.openRecord @value)}}
            />
          {{/if}}
          {{#if (and this.on.removeRecord (can 'update' @recordType))}}
            <Button
              @disabled={{@isDisabled}}
              @icon='close'
              @iconClass='u-color-error'
              @style='outline'
              {{on
                'click'
                (fn
                  this.on.removeRecord @value @model this.relationKind @property
                )
              }}
            />
          {{/if}}
        </div>
      {{else}}
        <div class={{bem 'add-new'}}>
          {{#if (and this.on.openRecordList (can 'update' @recordType))}}
            <Button
              @disabled={{@isDisabled}}
              @icon='search'
              @style='outline'
              {{on 'click' (fn this.on.openRecordList @recordType @property)}}
            />
          {{/if}}
          {{#if
            (and
              this.on.openNewRecord
              (can 'create' this.relationType parentModel=@model)
            )
          }}
            <Button
              @disabled={{@isDisabled}}
              @icon='add'
              @label={{t 'ui.form.field.belongs_to.new'}}
              @style='outline'
              {{on
                'click'
                (fn
                  this.on.openNewRecord
                  @model
                  this.relationKind
                  this.relationType
                  @property
                  @filters
                )
              }}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
    <Modal
      @onClose={{this.closeRecordList}}
      @dataDependencies={{this.isRecordListOpen}}
    >
      <this.RecordExplorerForAssign
        @filters={{@filters}}
        @onAssignRelation={{this.on.assignRecords}}
        @recordType={{this.relationType}}
        @options={{@options}}
        @parentRecordType={{getRecordType @model}}
        @relationType={{this.relationKind}}
        @scrollPosition={{this.scrollPositionAssign}}
        @onBodyScroll={{fn (mut this.scrollPositionAssign)}}
      />
    </Modal>
  </template>
}
